import * as yup from "yup";

import { DocumentationType } from "src/clients/flow-api";
import {
  DocumentationEntry,
  IntegrationType,
  OnePasswordLinkEntry,
  PromoteFlowVersionFormData,
  ProviderType,
} from "src/flowContainer/ManifestPromotion/types";

const INTEGRATION_TYPES: IntegrationType[] = ["general", "customer"];

const requiredForCustomer = (message: string) =>
  yup.string().when("integrationType", {
    is: "customer",
    then: (schema) => schema.required(message),
    otherwise: (schema) => schema.optional(),
  });

const DOCUMENTATION_TYPES: DocumentationType[] = [
  DocumentationType.PRODUCT_SPEC,
  DocumentationType.API_REFERENCE,
  DocumentationType.INTEGRATION_GUIDE,
  DocumentationType.OTHER,
];

const onePasswordLinkSchema: yup.ObjectSchema<OnePasswordLinkEntry> =
  yup.object({
    url: yup
      .string()
      .required("1Password link is required")
      .test(
        "is-1password-url",
        "Must be a valid 1Password link",
        (value) => !value || value.includes(".1password."),
      ),
  });

const documentationSchema: yup.ObjectSchema<DocumentationEntry> = yup.object({
  url: yup
    .string()
    .required("Documentation URL is required")
    .url("Must be a valid URL"),
  type: yup
    .string()
    .oneOf(DOCUMENTATION_TYPES)
    .required(
      "Documentation type is required",
    ) as yup.Schema<DocumentationType>,
});

export const promoteFlowVersionSchema: yup.ObjectSchema<PromoteFlowVersionFormData> =
  yup.object({
    integrationType: yup
      .string()
      .oneOf(INTEGRATION_TYPES)
      .required("Integration type is required") as yup.Schema<IntegrationType>,
    providerType: yup
      .string()
      .oneOf(["new", "existing"] as const)
      .required("Provider type is required") as yup.Schema<ProviderType>,
    existingProviderName: yup.string().when("providerType", {
      is: "existing",
      then: (schema) => schema.required("Please select an existing provider"),
      otherwise: (schema) => schema.optional(),
    }),
    newProviderDescription: yup.string().optional(),
    onePasswordLinks: yup.array().when("integrationType", {
      is: "general",
      then: (schema) => schema.of(onePasswordLinkSchema),
      otherwise: (schema) => schema.optional(),
    }),
    documentations: yup.array().when("integrationType", {
      is: "general",
      then: (schema) => schema.of(documentationSchema),
      otherwise: (schema) => schema.optional(),
    }),
    errorHandling: yup.string().optional(),
    comments: yup.string().optional(),
    // All four are written together by OrgAndWorkspaceDropdown, so only the
    // organization id carries a user-facing message. The conditions must stay
    // scoped to the customer branch: these fields are empty strings on a general
    // promotion, and a top-level requirement would block it on a hidden field.
    organizationId: yup.string().when("integrationType", {
      is: "customer",
      then: (schema) => schema.required("Select an organization and workspace"),
      otherwise: (schema) => schema.optional(),
    }),
    workspaceId: requiredForCustomer("Workspace is required"),
    organizationName: requiredForCustomer("Organization name is required"),
    workspaceName: requiredForCustomer("Workspace name is required"),
  });
